import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

// UTILS
import iconMap from 'utils/iconMap';

const InfoBubble = () => {
    const [isOpen, setIsOpen] = useState(false);    
    const [showSteps, setShowSteps] = useState(false);
    const bubbleRef = useRef(null);
    const isMobile = useMediaQuery({ maxWidth: 640 });

    const toggleInfo = () => {
        if (isOpen) return;
        setIsOpen(!isOpen);
        if (showSteps) setShowSteps(false);
    };

    const toggleSteps = () => {
        setShowSteps(!showSteps);
    };

    const closeInfo = () => {
        // Fermer d'abord la section "Comment faire ?" si elle est ouverte
        if (showSteps) {
            setShowSteps(false);
            // Attendre la fin de l'animation (500ms) avant de fermer l'infobulle
            setTimeout(() => {
                setIsOpen(false);
            }, 500);
        } else {
            setIsOpen(false);
        }
    };

    const handleClickOutside = (event) => {
        if (bubbleRef.current && !bubbleRef.current.contains(event.target)) {
            closeInfo();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const CommentInfoIcon = iconMap['commentInfo'];
    const CrossIcon = iconMap['crossSmall'];

    return (
        <>
        {!isMobile && (
            <div className="fixed bottom-4 right-4 z-50" ref={bubbleRef}>
                {/* Overlay avec backdrop blur */}
                {isOpen && (
                    <div 
                        className="fixed inset-0 bg-noir/20 backdrop-blur-sm"
                        onClick={closeInfo}
                        style={{
                            animation: 'fadeIn 0.3s ease-out',
                        }}
                    />
                )}
                
                <div className="relative flex items-end justify-end">
                    {/* Bouton rond - toujours visible */}
                    <button
                        onClick={toggleInfo}
                        className="liquid-glass-element w-14 h-14 rounded-full bg-emeraude text-white flex items-center justify-center shadow-2xl focus:outline-none relative group hover:scale-110 transition-transform duration-200"
                    >
                        <CommentInfoIcon className="size-7 fill-blanc drop-shadow-lg" />
                    </button>

                    {/* Carte de contenu qui émerge */}
                    <div
                        className={`liquid-glass-element absolute bottom-0 right-0 w-80 bg-emeraude rounded-2xl shadow-2xl p-4 transition-all duration-500 ease-out ${
                            isOpen
                                ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                                : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
                        }`}
                        style={{
                            transformOrigin: 'bottom right',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col">
                            <div className="grid grid-cols-[1fr_auto] gap-3 items-start mb-3">
                                {/* Texte principal */}
                                <div className="text-sm text-left whitespace-normal text-blanc">
                                    <p className="leading-relaxed">
                                        Si vous rencontrez un problème quelconque ou avez une suggestion, veuillez ouvrir un ticket IRRIS.
                                    </p>
                                </div>

                                {/* Bouton fermer */}
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        closeInfo();
                                    }} 
                                    className="flex-shrink-0 p-1 rounded-lg hover:bg-emeraude-80 transition-colors duration-200 group/close"
                                >
                                    <CrossIcon className="w-5 h-5 fill-blanc group-hover/close:rotate-90 transition-transform duration-300" />
                                </button>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                                <a
                                    href="https://irris.altitudeinfra.fr/#/tickets/create/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="liquid-glass-element px-3 py-2 bg-blanc dark:bg-noir-15 rounded-lg border border-gris/30 hover:border-gris/50 focus:outline-none text-xs text-noir dark:text-blanc poppins-regular text-center transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    Ouvrir un ticket IRRIS
                                </a>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSteps();
                                    }}
                                    className="text-xs text-blanc/90 hover:text-blanc underline focus:outline-none text-left transition-colors duration-200 self-start"
                                >
                                    Comment faire ?
                                </button>
                            </div>

                            {/* Section étapes - animation conservée */}
                            <div 
                                className={`mt-3 overflow-hidden transition-all duration-500 ease-out ${
                                    showSteps 
                                        ? 'max-h-48 opacity-100' 
                                        : 'max-h-0 opacity-0'
                                }`}
                            >
                                <div className="text-xs bg-noir/30 backdrop-blur-sm rounded-xl p-3 text-left border border-blanc/10">
                                    <ol className="list-decimal list-inside space-y-1.5 text-blanc/90">
                                        <li>Cliquer sur le bouton "Ouvrir un ticket IRRIS" au-dessus.</li>
                                        <li>Sélectionner "Type de ticket" : DSI Helpdesk.</li>
                                        <li>Sélectionner "Catégorie" : Application.</li>
                                        <li>Sélectionner "Sous-catégorie" : Assistance logiciel ou Problème Logiciel.</li>
                                        <li>Choisir "Application concernée" : Hub Altitude Infra.</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
        <style>{`
            @keyframes fadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
            @keyframes pulse {
                0%, 100% {
                    opacity: 1;
                }
                50% {
                    opacity: 0.7;
                }
            }
        `}</style>
        </>
    );
};

export default InfoBubble;
